import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/15"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/d3a1968f7eec1c476a5eb165d72b1db4.jpg')`,
            objectPosition: 'center'
          }}
        ></div>
        
        <div className={`relative z-10 text-center px-6 max-w-4xl transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-5xl md:text-7xl font-light mb-6 text-[#4A4A4A] tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Our Story
          </h2>
          <p className="text-lg md:text-xl font-light text-[#6B6B6B] max-w-2xl mx-auto leading-relaxed">
            A journey of passion, craftsmanship, and timeless beauty
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="w-full h-[600px] rounded-3xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=elegant%20woman%20wearing%20minimalist%20luxury%20gold%20jewelry%20delicate%20necklace%20and%20earrings%20in%20natural%20warm%20daylight%20soft%20focus%20contemporary%20lifestyle%20photography%20warm%20beige%20cream%20tones%20sophisticated%20aesthetic%20fine%20jewelry%20close%20up%20portrait%20high%20end%20brand%20visual&width=800&height=1000&seq=story001lux&orientation=portrait"
              alt="Brand Story"
              className="w-full h-full object-cover object-top"
            />
          </div>
          
          <div>
            <p className="text-sm font-light text-[#C9A961] tracking-widest mb-6">THE BEGINNING</p>
            <h3 className="text-4xl md:text-5xl font-light mb-8 text-[#4A4A4A] leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Crafted with Intention
            </h3>
            <div className="space-y-6 text-lg font-light text-[#6B6B6B] leading-loose">
              <p>
                LUMIÈRE was born from a pure pursuit of beauty. Our designers draw inspiration from natural light and shadow, architectural lines, and feminine strength to create pieces that are both modern and timeless.
              </p>
              <p>
                Each piece of jewelry undergoes dozens of meticulous processes, using premium materials and completed by experienced craftsmen. We don't chase extravagance, but focus only on designs that can accompany you for a lifetime.
              </p>
              <p>
                This is jewelry created for contemporary women who appreciate details and cherish quality—understated, warm, and full of strength.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-light text-[#C9A961] tracking-widest mb-6">OUR PHILOSOPHY</p>
          <h3 className="text-4xl md:text-5xl font-light mb-8 text-[#4A4A4A] leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Restraint is the Ultimate Luxury
          </h3>
          <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-6">
            We believe that true luxury comes from restraint. Each piece is carefully crafted, removing complexity and preserving essence.
          </p>
          <p className="text-lg font-light text-[#6B6B6B] leading-loose">
            Designed for contemporary women, fitting every scene of life—from morning coffee moments to elegant evening gatherings.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-sm font-light text-[#C9A961] tracking-widest mb-4">OUR VALUES</p>
            <h3 className="text-4xl md:text-5xl font-light text-[#4A4A4A]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              What We Stand For
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 text-[#C9A961]">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h4 className="text-xl font-light text-[#4A4A4A] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Craftsmanship
              </h4>
              <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                Every piece is handcrafted by skilled artisans who pour their expertise and passion into each detail.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 text-[#C9A961]">
                <i className="ri-leaf-line text-4xl"></i>
              </div>
              <h4 className="text-xl font-light text-[#4A4A4A] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Sustainability
              </h4>
              <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                We source ethically and create pieces designed to last generations, not seasons.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 text-[#C9A961]">
                <i className="ri-star-line text-4xl"></i>
              </div>
              <h4 className="text-xl font-light text-[#4A4A4A] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Timeless Design
              </h4>
              <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                Our designs transcend trends, offering beauty that remains relevant through the years.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&display=swap');
      `}</style>
    </div>
  );
}
